/**
 * What Адреси за заключване is asked for, and the pure rules its screen works by.
 *
 * The list is the server's, a page at a time, ordered and searched there (the firm that
 * imported its previous system has hundreds of doors waiting), so the screen's state is a
 * filter, an order, a page and a search — written here once, for the request, for the file
 * and for the way back from Settings → Адреси.
 *
 * DOM-free, and it does not import the `../ui` barrel, so the client tests read it directly.
 */

import type { Schema } from '@taxi/api-client';

import type { SourceFilter } from './back';

/** One booking or imported address of a row, as the members read gives it. */
export type UnplacedMember = Schema<'UnplacedMember'>;

/**
 * Which of Внесени's addresses are listed: those waiting with the point the previous system had
 * them at, or those it had no point for — nothing to open the map on, so they can only be
 * joined to a place or deleted. The server's names, `Waiting` its default.
 */
export type ImportedView = 'Waiting' | 'NoPoint';

export const importedViews: readonly ImportedView[] = ['Waiting', 'NoPoint'];

/** How the rows are ordered. The server's names, `Bookings` its default. */
export type AddressSort = 'Bookings' | 'Latest' | 'Address' | 'Callers';

export const addressSorts: readonly AddressSort[] = ['Bookings', 'Latest', 'Address', 'Callers'];

/** The screen's state, beyond the period. */
export interface AddressesView {
  source: SourceFilter;
  /** Under Внесени only; every other filter lists booked rows, which always have a point. */
  view: ImportedView;
  sort: AddressSort;
  /** From 0. */
  page: number;
  /** What was typed into the search, trimmed. */
  q: string;
}

/** The view a fresh visit opens on. */
export const defaultAddressesView: AddressesView = { source: 'all', view: 'Waiting', sort: 'Bookings', page: 0, q: '' };

/**
 * The view as query parameters, leaving out whatever is the default, so a link and a request
 * for the plain list stay as short as they were.
 */
export function addressesParams(view: Partial<AddressesView>): URLSearchParams {
  const params = new URLSearchParams();
  const { source = 'all', view: shown = 'Waiting', sort = 'Bookings', page = 0, q = '' } = view;

  if (source !== 'all') params.set('source', source);
  if (source === 'Imported' && shown === 'NoPoint') params.set('view', shown);
  if (sort !== 'Bookings') params.set('sort', sort);
  if (page > 0) params.set('page', String(Math.floor(page)));
  if (q.trim() !== '') params.set('q', q.trim());

  return params;
}

/** The view as `&name=value` pairs for the report's request and its file; empty for the defaults. */
export function addressesQuery(view: Partial<AddressesView>): string {
  const params = addressesParams(view).toString();

  return params === '' ? '' : `&${params}`;
}

/** How far from the door a member may be and be ticked without asking: the reach of a link (CustomerService.LinkMetres). */
export const TickedMetres = 200;

/** Past this a member is another town, and the server refuses it (UnplacedPickups.GroupMetres). */
export const FarMetres = 2_000;

/** Where a member stands in the members list: whether it starts ticked, and whether it may be. */
export interface MemberTick {
  id: string;
  ticked: boolean;
  disabled: boolean;
  /** From the door, or the row's point while there is no door yet; null when either has no point. */
  metres: number | null;
}

/** A point, as the members list and the row carry one. */
export interface Spot {
  longitude: number | null;
  latitude: number | null;
}

/**
 * Which members start ticked.
 *
 * Grouping is a reading of words, so the administrator sees each member and may untick it;
 * these are the defaults. Live and already linked ones cannot be changed from here, nor one
 * past `FarMetres`, which the server would refuse; one between `TickedMetres` and `FarMetres`
 * starts unticked, to be looked at. Measured from the door the address is being joined to, or
 * the row's own point before one is chosen; a member with no point is judged by its words alone.
 */
export function defaultTicked(members: readonly UnplacedMember[], from: Spot | null): MemberTick[] {
  return members.map((member) => {
    const metres = distance(member, from);
    const disabled = !member.eligible || (metres !== null && metres > FarMetres);

    return { id: member.id, ticked: !disabled && (metres === null || metres <= TickedMetres), disabled, metres };
  });
}

/**
 * How many bookings and addresses a join from this door would take by default — the ticks of
 * `defaultTicked`, counted by kind — so what a switch promises is what the save then joins.
 */
export function defaultJoinCounts(
  members: readonly UnplacedMember[],
  from: Spot | null,
): { orders: number; addresses: number } {
  const picked = tickedIds(defaultTicked(members, from));
  const chosen = members.filter((member) => picked.has(member.id));

  return {
    orders: chosen.filter((member) => member.kind !== 'Saved').length,
    addresses: chosen.filter((member) => member.kind === 'Saved').length,
  };
}

/** Great-circle metres, on the sphere the server measures on (GeoPoint.DistanceTo). */
export function metresBetween(a: { longitude: number; latitude: number }, b: { longitude: number; latitude: number }): number {
  const radians = (degrees: number) => (degrees * Math.PI) / 180;
  const dLat = radians(b.latitude - a.latitude);
  const dLon = radians(b.longitude - a.longitude);
  const h =
    Math.sin(dLat / 2) ** 2 + Math.cos(radians(a.latitude)) * Math.cos(radians(b.latitude)) * Math.sin(dLon / 2) ** 2;

  return 6_371_000 * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

function distance(member: Spot, from: Spot | null): number | null {
  if (from === null || from.longitude === null || from.latitude === null) return null;
  if (member.longitude === null || member.latitude === null) return null;

  return metresBetween(
    { longitude: member.longitude, latitude: member.latitude },
    { longitude: from.longitude, latitude: from.latitude },
  );
}

/** A join, as the places API takes it. */
export type PlaceJoinRequest = Schema<'PlaceJoinRequest'>;

/** What a join did, as the places API answers. */
export type PlaceJoinResult = Schema<'PlaceJoinResult'>;

/** The row a join is made from: its key and words, the filter it was listed under, and the period read. */
export interface JoinRow {
  key: string;
  line: string;
  source: SourceFilter;
  /** The period's first day; null for an imported row, which has none. */
  from: string | null;
  to: string | null;
}

/**
 * The join of the ticked members, each id under its own kind: a booking's pickup is its
 * order's id, a destination is its stop's, an imported address is the address's.
 */
export function joinRequest(
  joinId: string,
  row: JoinRow,
  members: readonly UnplacedMember[],
  ticked: ReadonlySet<string>,
): PlaceJoinRequest {
  const chosen = members.filter((member) => ticked.has(member.id));
  const of = (kind: string) => chosen.filter((member) => member.kind === kind).map((member) => member.id);
  const imported = row.source === 'Imported';

  return {
    joinId,
    key: row.key,
    typed: row.line,
    source: row.source === 'all' ? null : row.source,
    from: imported ? null : row.from,
    to: imported ? null : row.to,
    addressIds: of('Saved'),
    orderIds: of('Pickup'),
    destinationIds: of('Destination'),
  };
}

/** What a join left alone, counted by why. */
export function skippedCounts(result: Pick<PlaceJoinResult, 'skipped'>): { live: number; linked: number; far: number; gone: number } {
  const count = (reason: string) => result.skipped.filter((skip) => skip.reason === reason).length;

  return { live: count('Live'), linked: count('Linked'), far: count('Far'), gone: count('Gone') };
}

/** Why a join left an end alone, as the notice words it. */
export type SkipReason = 'live' | 'linked' | 'far' | 'gone';

/**
 * The reasons a join left anything alone, each with how many, in a fixed order and only those
 * that happened — so a join whose only skips were gone ones does not read "0 in progress, 0
 * already locked, 0 far away".
 */
export function skippedParts(result: Pick<PlaceJoinResult, 'skipped'>): { reason: SkipReason; count: number }[] {
  const counts = skippedCounts(result);

  return (['live', 'linked', 'far', 'gone'] as const)
    .filter((reason) => counts[reason] > 0)
    .map((reason) => ({ reason, count: counts[reason] }));
}

/**
 * What a `counts.*` noun is asked with: the number itself, for i18next to choose the plural,
 * and the number as the firm writes it, to show. A count passed as a formatted string is never
 * pluralised, which is how "1 адреса на 1 клиенти" happens.
 */
export function countOptions(value: number, count: (value: number) => string): { count: number; shown: string } {
  return { count: value, shown: count(value) };
}

/** Which part of the members list a member is shown under. */
export type MemberGroup = 'inPeriod' | 'outsidePeriod' | 'callersAddresses';

export function memberGroup(member: Pick<UnplacedMember, 'kind' | 'inPeriod'>): MemberGroup {
  if (member.kind === 'Saved') return 'callersAddresses';

  return member.inPeriod ? 'inPeriod' : 'outsidePeriod';
}

/** The ticks as a set of ids, the members that may not be chosen never among them. */
export function tickedIds(ticks: readonly MemberTick[]): Set<string> {
  return new Set(ticks.filter((tick) => tick.ticked && !tick.disabled).map((tick) => tick.id));
}

/** Bookings to take off the list, or to put back, each id under its own kind. */
export interface DismissIds {
  pickups: string[];
  destinations: string[];
}

/**
 * Every booking of a row, for Премахни от списъка: in the period and before it alike, so the
 * row stays off whatever period is read next. A booking's pickup is its order's id, a
 * destination its stop's; an imported address is no booking, and is never among them.
 */
export function dismissIds(members: readonly Pick<UnplacedMember, 'kind' | 'id'>[]): DismissIds {
  const of = (kind: string) => members.filter((member) => member.kind === kind).map((member) => member.id);

  return { pickups: of('Pickup'), destinations: of('Destination') };
}

/**
 * Whether Изтрий may take an imported address: one used from this system since it was imported
 * is the firm's own record now, and is kept — the server keeps it too, and says so.
 */
export function removable(member: Pick<UnplacedMember, 'kind' | 'usedHere'>): boolean {
  return member.kind === 'Saved' && member.usedHere !== true;
}

/** Each reason a place is offered for a row, as the server names it (Geo's SimilarKind, `SimilarKind` in `../places/api`). */
const REASONS: Record<string, string> = {
  JoinedBefore: 'joinedBefore',
  Same: 'same',
  Building: 'building',
  Words: 'words',
  Name: 'name',
  Near: 'near',
  Search: 'search',
};

/**
 * The key a reason's words are under, `reports.addresses.reason.<this>` — for the candidates in
 * Присъедини… and the suggestion on a row alike. A reason this console does not know yet reads
 * as a search, the plainest of them, rather than as a missing key.
 */
export function reasonKey(kind: string, metres?: number | null): string {
  // "The same address" is a claim only near the door: further away it is the same name, which a
  // chain's other branch also has — магазин Абц-дунав and the магазин Абц across town.
  if (kind === 'Same' && metres != null && metres > SAME_PLACE_METRES) return 'sameName';

  return Object.hasOwn(REASONS, kind) ? REASONS[kind] : 'search';
}

/** How far "the same address" may be from the row and still be called so — the link distance. */
export const SAME_PLACE_METRES = 200;
